import React, { FC, useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'

import { DecorationTitle, StandardButton } from 'src/components/atoms'
import { AttributeMappingInput, MetadataSourceInput } from 'src/components/molecules'

import { yup, BaseYup, yupResolver } from 'src/customYup'
import { useGetSamlIdp, useSamlIdp, useToast } from 'src/hooks'
import { CompanyEntity, SamlIdpEntity, SamlIdpFormEntity } from 'src/types'

type Props = {
  company: CompanyEntity
  samlIdp: SamlIdpEntity
  className?: string
}

const schema: yup.SchemaOf<SamlIdpFormEntity> = BaseYup.object({
  idpSignout: BaseYup.boolean().required(),
  metadataSourceType: BaseYup.mixed().oneOf(['metadataURL', 'metadataFile']).required(),
  metadata: BaseYup.object({
    metadataFile: BaseYup.string().optional(),
    metadataFileName: BaseYup.string().optional(),
    metadataURL: BaseYup.string().optional(),
  }).required(),
  attributeMapping: BaseYup.object({
    email: BaseYup.string().required(),
    name: BaseYup.string().optional(),
    department: BaseYup.string().optional(),
  }).required(),
})

export const IdentityProviderSetting: FC<Props> = ({ company, samlIdp, className }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const { refetch } = useGetSamlIdp()
  const { createSamlIdp, updateSamlIdp, toFormEntity } = useSamlIdp()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()

  const { setValue, register, watch, reset, handleSubmit } = useForm<SamlIdpFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: toFormEntity(samlIdp),
  })

  useEffect(() => {
    // SAMLの設定をリセットした後、react-hook-formの値もリセットする
    const isAfterResetSamlSetting =
      !samlIdp.samlIdp.metadataFile &&
      !samlIdp.samlIdp.metadataFileName &&
      !samlIdp.samlIdp.metadataURL
    if (isAfterResetSamlSetting) {
      reset(toFormEntity(samlIdp))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, samlIdp])

  const baseFormEntity: SamlIdpFormEntity = {
    idpSignout: false,
    metadataSourceType: watch('metadataSourceType'),
    metadata: {
      metadataFile: watch('metadata.metadataFile'),
      metadataFileName: watch('metadata.metadataFileName'),
      metadataURL: watch('metadata.metadataURL'),
    },
    attributeMapping: {
      email: watch('attributeMapping.email'),
      name: watch('attributeMapping.name'),
      department: watch('attributeMapping.department'),
    },
  }

  const submit = async (formEntity: SamlIdpFormEntity) => {
    setIsUpdating(true)

    try {
      await (samlIdp.samlIdp.id
        ? updateSamlIdp(company.id, samlIdp.samlIdp.id, formEntity)
        : createSamlIdp(company.id, baseFormEntity))
      displayStandardTextToast('IdP設定を変更しました')
      setIsEditMode(false)
    } catch {
      displayErrorTextToast('IdP設定の変更に失敗しました')
    } finally {
      setIsUpdating(false)
      refetch()
    }
  }

  const isEnabledRegisterButton =
    !isEditMode &&
    ((watch('metadataSourceType') === 'metadataFile' && !!watch('metadata.metadataFile')) ||
      (watch('metadataSourceType') === 'metadataURL' && !!watch('metadata.metadataURL'))) &&
    !!watch('attributeMapping.email')

  return (
    <div className={className}>
      <DecorationTitle>IdP</DecorationTitle>
      <form onSubmit={handleSubmit(submit)}>
        <MetadataSourceInput
          register={register}
          setValue={setValue}
          watch={watch}
          className="tw-mt-s3 tw-w-[38rem]"
        />
        <AttributeMappingInput register={register} className="tw-mt-s3" />
        <div className="tw-mt-s2 tw-flex tw-items-center">
          <StandardButton
            text="登録"
            prependIconPath="check/soft/line"
            buttonType="primary"
            isLoading={isUpdating}
            type="submit"
            disabled={!isEnabledRegisterButton}
          />
        </div>
      </form>
    </div>
  )
}
